// assets/cd-product.js — protein78 ürün sayfası davranışları (modül; tarayıcı bir kez yükler).
// 1) Varyant değişince [data-cd-sync] alanlarını bölüm yanıtından yeniler (servis başı fiyat, kargo çubuğu).
// 2) Hediye seçiminde özet satırını yeniler.
// 3) Sepet değişince ücretsiz kargo çubuğunu /cart.js toplamıyla yeniler.
// 4) <cd-bundle>: "Birlikte al" seçimi, canlı toplam, toplu sepete ekleme (Cart API) ve öneri yüklemesi.
// 5) "#cd-..." çapalarına gidince kapalı akordeonu açar.
import { StandardEvents, CartLinesUpdateEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

/** @param {number} cents @param {HTMLElement} el */
const money = (cents, el) =>
  formatMoney(Math.round(cents), el.dataset.moneyFormat || '{{amount}}', el.dataset.currency || 'TRY');

const routes = () => /** @type {any} */ (window).Theme?.routes ?? {};

async function fetchCart() {
  const response = await fetch(`${routes().cart_url ?? '/cart'}.js`, {
    headers: { Accept: 'application/json' },
    credentials: 'same-origin',
  });
  if (!response.ok) throw new Error(`cart ${response.status}`);
  return response.json();
}

/* ---------- Ücretsiz kargo çubuğu ---------- */

/** @param {HTMLElement} el @param {number} cartTotal */
function renderShipping(el, cartTotal) {
  const threshold = Number(el.dataset.threshold) || 0;
  if (threshold <= 0) return;
  const price = Number(el.dataset.price) || 0;
  el.dataset.cartTotal = String(cartTotal);

  let template = el.dataset.textRemaining || '';
  let done = false;
  if (cartTotal >= threshold) {
    template = el.dataset.textDone || '';
    done = true;
  } else if (cartTotal + price >= threshold) {
    template = el.dataset.textAdd || '';
  }
  const remaining = Math.max(0, threshold - cartTotal - price);
  const text = template.replace('[kalan]', money(remaining, el)).replace('[esik]', money(threshold, el));
  const ratio = Math.min(100, Math.round((cartTotal / threshold) * 100));
  // Bu ürünün katkısı: eşiğe kadar ayrı (yeşil) dilim; sepet eşiği geçtiyse 0
  const addRatio = done ? 0 : Math.min(100, Math.round(((cartTotal + price) / threshold) * 100)) - ratio;

  const textEl = el.querySelector('[data-cd-ship-text]');
  if (textEl) textEl.textContent = text;
  const bar = el.querySelector('[data-cd-ship-bar]');
  if (bar) {
    bar.setAttribute('aria-valuenow', String(ratio));
    const fill = bar.querySelector('.cd-ship__fill');
    if (fill instanceof HTMLElement) fill.style.inlineSize = `${ratio}%`;
    const add = bar.querySelector('[data-cd-ship-add]');
    if (add instanceof HTMLElement) add.style.inlineSize = `${Math.max(0, addRatio)}%`;
  }
  el.classList.toggle('is-done', done);
  el.classList.toggle('is-add', !done && cartTotal + price >= threshold);
}

async function refreshShipping() {
  const bars = document.querySelectorAll('[data-cd-ship]');
  if (bars.length === 0) return;
  try {
    const cart = await fetchCart();
    for (const el of bars) {
      if (el instanceof HTMLElement) renderShipping(el, Number(cart.total_price) || 0);
    }
  } catch (error) {
    console.warn('[cd-product] kargo çubuğu yenilenemedi', error);
  }
}

/* ---------- Kesim saati satırı (docs/07 K4): saat geçtiyse "genellikle 24 saat içinde" ---------- */

function applyCutoff() {
  let now;
  try {
    const parts = new Intl.DateTimeFormat('en-GB', {
      timeZone: 'Europe/Istanbul',
      hour: '2-digit',
      minute: '2-digit',
      hourCycle: 'h23',
    }).formatToParts(new Date());
    const get = (/** @type {string} */ type) => Number(parts.find((p) => p.type === type)?.value ?? 0);
    now = get('hour') * 60 + get('minute');
  } catch {
    return;
  }
  for (const el of document.querySelectorAll('[data-cd-cutoff]')) {
    if (!(el instanceof HTMLElement) || el.dataset.cdCutoffDone) continue;
    const [h, m] = (el.dataset.cdCutoff ?? '').split(':').map(Number);
    if (!Number.isFinite(h)) continue;
    el.dataset.cdCutoffDone = '1';
    if (now < h * 60 + (Number.isFinite(m) ? m : 0)) continue;
    const title = el.dataset.afterTitle ?? '';
    if (!title) continue;
    const b = document.createElement('b');
    b.textContent = title;
    el.replaceChildren(b, document.createTextNode(el.dataset.afterText ?? ''));
  }
}

applyCutoff();

/* ---------- Belge düzeyi dinleyiciler (tek kez) ---------- */

const flag = '__cdProductInit';
if (!(/** @type {any} */ (window)[flag])) {
  /** @type {any} */ (window)[flag] = true;

  // Varyant değişimi
  document.addEventListener(StandardEvents.productSelect, (event) => {
    const target = event.target;
    if (!(target instanceof Element) || target.closest('product-card')) return;
    const promise = /** @type {any} */ (event).promise;
    if (!promise) return;
    promise
      .then((/** @type {any} */ result) => {
        const detail = result?.detail;
        const html = detail?.html;
        if (html && typeof html.querySelector === 'function') {
          for (const el of document.querySelectorAll('[data-cd-sync]')) {
            if (!(el instanceof HTMLElement)) continue;
            const id = el.dataset.cdSync ?? '';
            const next = html.querySelector(`[data-cd-sync="${CSS.escape(id)}"]`);
            if (next) el.replaceWith(document.importNode(next, true));
          }
        }
        applyCutoff();
        const variant = detail?.resource;
        if (variant?.id) {
          for (const bundle of document.querySelectorAll('cd-bundle')) {
            if (bundle instanceof CdBundle) bundle.setMainVariant(variant);
          }
        }
      })
      .catch((/** @type {any} */ error) => {
        if (error?.name !== 'AbortError') console.warn('[cd-product] varyant eşlemesi', error);
      });
  });

  // Sepet değişimi → kargo çubuğu
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    const promise = /** @type {any} */ (event).promise;
    if (promise) {
      promise.then(refreshShipping).catch(() => {});
    } else {
      refreshShipping();
    }
  });

  // Hediye seçimi özeti
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const gift = input.closest('[data-cd-gift]');
    if (!gift) return;
    const selected = gift.querySelector('[data-cd-gift-selected]');
    if (selected) selected.textContent = input.value;
  });

  // "#cd-..." çapası → kapalı akordeonu aç
  document.addEventListener('click', (event) => {
    const link = event.target instanceof Element ? event.target.closest('a[href^="#cd-"]') : null;
    if (!link) return;
    const id = link.getAttribute('href')?.slice(1);
    const target = id ? document.getElementById(id) : null;
    const details = target?.closest('details');
    if (details && !details.open) details.open = true;
  });

  // bfcache dönüşü: sepet başka sekmede değişmiş olabilir
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshShipping();
  });
}

/* ---------- Birlikte al ---------- */

class CdBundle extends HTMLElement {
  #abort = new AbortController();
  #busy = false;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;
    this.addEventListener('change', () => this.update(), { signal });
    this.addEventListener(
      'click',
      (event) => {
        if (event.target instanceof Element && event.target.closest('[data-cd-bundle-add]')) this.add();
      },
      { signal }
    );
    if (this.dataset.state === 'loading' && this.dataset.fetchUrl) {
      this.#load(this.dataset.fetchUrl, signal);
    } else {
      this.update();
    }
  }

  disconnectedCallback() {
    this.#abort.abort();
  }

  /** @param {string} url @param {AbortSignal} signal */
  async #load(url, signal) {
    try {
      const response = await fetch(url, { signal });
      const text = await response.text();
      const doc = new DOMParser().parseFromString(text, 'text/html');
      const fresh = doc.querySelector(`cd-bundle[data-block-id="${CSS.escape(this.dataset.blockId ?? '')}"]`);
      const list = this.querySelector('.cd-bundle__list');
      const extras = fresh ? [...fresh.querySelectorAll('[data-cd-bundle-item]:not([data-main])')] : [];
      if (fresh instanceof HTMLElement && fresh.dataset.state === 'ready' && list && extras.length > 0) {
        // Öneri yanıtı yalnız ek ürünleri getirir; ana ürün satırı sayfadaki gibi kalır.
        const productId = this.dataset.productId;
        for (const item of extras) {
          if (productId && item.querySelector(`#cd-bundle-${CSS.escape(productId)}`)) continue;
          const plus = fresh.querySelector('.cd-bundle__plus');
          if (plus) list.append(document.importNode(plus, true));
          list.append(document.importNode(item, true));
        }
        this.dataset.state = 'ready';
        this.update();
      } else {
        this.dataset.state = 'empty';
      }
    } catch (error) {
      if (/** @type {any} */ (error)?.name !== 'AbortError') this.dataset.state = 'empty';
    }
  }

  /** @param {{id: number|string, price: number, title?: string, available?: boolean}} variant */
  setMainVariant(variant) {
    const main = this.querySelector('[data-cd-bundle-item][data-main]');
    if (!(main instanceof HTMLElement)) return;
    main.dataset.variantId = String(variant.id);
    if (typeof variant.price === 'number') {
      main.dataset.price = String(variant.price);
      const priceEl = main.querySelector('[data-cd-bundle-item-price]');
      if (priceEl) priceEl.textContent = money(variant.price, this);
    }
    const title = main.querySelector('[data-cd-bundle-main-title]');
    if (title && variant.title) title.textContent = variant.title;
    this.update();
  }

  /** @returns {Array<{el: HTMLElement, id: string, price: number}>} */
  #selected() {
    /** @type {Array<{el: HTMLElement, id: string, price: number}>} */
    const out = [];
    for (const el of this.querySelectorAll('[data-cd-bundle-item]')) {
      if (!(el instanceof HTMLElement)) continue;
      const check = el.querySelector('[data-cd-bundle-check]');
      const isOn = el.hasAttribute('data-main') || (check instanceof HTMLInputElement && check.checked);
      el.classList.toggle('is-off', !isOn);
      if (!isOn) continue;
      let id = el.dataset.variantId ?? '';
      let price = Number(el.dataset.price) || 0;
      const select = el.querySelector('[data-cd-bundle-variant]');
      if (select instanceof HTMLSelectElement) {
        const option = select.selectedOptions[0];
        id = select.value;
        price = Number(option?.dataset.price) || price;
        const priceEl = el.querySelector('[data-cd-bundle-item-price]');
        if (priceEl) priceEl.textContent = money(price, this);
      }
      out.push({ el, id, price });
    }
    return out;
  }

  update() {
    if (this.dataset.state !== 'ready') return;
    const all = this.querySelectorAll('[data-cd-bundle-item]').length;
    const selected = this.#selected();
    const count = selected.length;
    const sum = selected.reduce((total, item) => total + item.price, 0);
    const discount = Number(this.dataset.discount) || 0;
    const full = count === all && all > 1;

    const title = this.querySelector('[data-cd-bundle-title]');
    if (title) title.textContent = (this.dataset.totalTemplate ?? '').replace('[sayi]', String(count));

    const sumEl = this.querySelector('[data-cd-bundle-sum]');
    const priceEl = this.querySelector('[data-cd-bundle-price]');
    const saving = this.querySelector('[data-cd-bundle-saving]');

    if (discount > 0 && priceEl) {
      const bundlePrice = full ? Math.round((sum * (100 - discount)) / 100) : sum;
      if (sumEl) sumEl.textContent = money(sum, this);
      priceEl.textContent = money(bundlePrice, this);
      this.classList.toggle('is-partial', !full);
      if (saving instanceof HTMLElement) {
        saving.hidden = !full;
        const span = saving.querySelector('span');
        if (span) span.textContent = (this.dataset.savingTemplate ?? '').replace('[tutar]', money(sum - bundlePrice, this));
      }
    } else if (sumEl) {
      sumEl.textContent = money(sum, this);
    }

    const buttonText = this.querySelector('[data-cd-bundle-button-text]');
    if (buttonText) buttonText.textContent = (this.dataset.buttonTemplate ?? '').replace('[sayi]', String(count));
  }

  async add() {
    if (this.#busy) return;
    const selected = this.#selected().filter((item) => item.id);
    if (selected.length === 0) return;

    const button = this.querySelector('[data-cd-bundle-add]');
    const errorEl = this.querySelector('[data-cd-bundle-error]');
    if (errorEl instanceof HTMLElement) errorEl.hidden = true;
    this.#busy = true;
    if (button instanceof HTMLButtonElement) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const giftProperty = this.dataset.giftProperty || 'Hediye';
    const giftInput = document.querySelector(`[data-cd-gift] input[name="properties[${CSS.escape(giftProperty)}]"]:checked`);
    const items = selected.map((item) => {
      /** @type {{id: number, quantity: number, properties?: Record<string, string>}} */
      const line = { id: Number(item.id), quantity: 1 };
      if (item.el.hasAttribute('data-main') && giftInput instanceof HTMLInputElement && giftInput.value) {
        line.properties = { [giftProperty]: giftInput.value };
      }
      return line;
    });

    const sectionIds = [];
    for (const el of document.querySelectorAll('cart-items-component')) {
      if (el instanceof HTMLElement && el.dataset.sectionId) sectionIds.push(el.dataset.sectionId);
    }

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    try {
      const response = await fetch(routes().cart_add_url ?? '/cart/add', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const data = await response.json();
      const cart = await fetchCart();
      const didError = Boolean(data?.status);
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'cd-bundle',
          sourceId: this.dataset.blockId ?? '',
          itemCount: items.length,
          productId: this.dataset.productId ?? '',
          sections: didError ? undefined : data?.sections,
          didError,
        },
      });
      if (didError) this.#showError(data?.description || data?.message);
    } catch (error) {
      deferred.reject(error);
      this.#showError();
    } finally {
      this.#busy = false;
      if (button instanceof HTMLButtonElement) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  }

  /** @param {string} [message] */
  #showError(message) {
    const errorEl = this.querySelector('[data-cd-bundle-error]');
    if (!(errorEl instanceof HTMLElement)) return;
    errorEl.textContent = message || this.dataset.errorText || '';
    errorEl.hidden = false;
  }
}

if (!customElements.get('cd-bundle')) customElements.define('cd-bundle', CdBundle);
